import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { UsersIcon, PlusIcon } from 'lucide-react'
import { trpc } from '@/main'
import { useIsMobile } from '@/hooks/use-mobile'
import { useHydrated } from '@/hooks/use-hydrated'
import { useTeam } from '@/hooks/use-team'
import { usePermissions } from '@/hooks/use-permissions'
import { useRootSearch } from '@/hooks/use-root-search'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command'
import { SectionLabel } from '@/components/custom/section-label'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ListPanelBodySkeleton, MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { AnimatedOutlet } from './-components/animated-outlet'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { Avatar } from './-components/shared/avatar'
import { ReportTabs } from './-components/report/report-tabs'
import { TourDialog } from './-components/teams/tour-dialog'
import { TeamTreeItem } from './-components/teams/team-tree-item'
import { TeamsMembersPanel } from './-components/teams/teams-members-panel'
import { TeamActionMenu } from './-components/teams/team-action-menu'

export const Route = createFileRoute('/_app/teams')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.teams.getTeams.queryOptions({}))
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()

  return (
    <div id="report" className="@container flex h-full flex-col">
      <TourDialog />
      <PageLayout
        icon={NAVIGATION_ICONS.teams}
        title={t`Teams`}
        breadcrumbsSlot={<TeamsBreadcrumbs />}
        titleActions={<TeamsTitleActions />}
      >
        <TeamsLayout />
      </PageLayout>
    </div>
  )
}

const TeamForm = lazy(() => import('./-components/teams/team-form').then((m) => ({ default: m.TeamForm })))

function TeamsLayout() {
  const { teamId } = useParams({ strict: false })
  // Gate the viewport-driven auto-collapse on hydration: the panel body hydrates inside a Suspense
  // boundary, so flipping collapse from isMobile mid-hydration races that boundary and mismatches.
  const hydrated = useHydrated()
  const isMobile = useIsMobile() && hydrated

  const hasTeamSelected = !!teamId

  const teamDialog = useSearchParamDialog('editTeam')
  const editTeamId = typeof teamDialog.value === 'number' ? teamDialog.value.toString() : undefined

  return (
    <CollapsiblePanelProvider defaultCollapsed={isMobile && hasTeamSelected}>
      <Suspense fallback={null}>
        {teamDialog.mounted && (
          <Dialog open={teamDialog.open} onOpenChange={teamDialog.onOpenChange}>
            <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
              <TeamForm teamId={editTeamId} onOpenChange={teamDialog.onOpenChange} />
            </DialogContent>
          </Dialog>
        )}
      </Suspense>

      <div className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* Left Panel - Teams Tree (always visible, squashes to a 52px icon column when a team is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <CollapsibleListPanel isOpen={hasTeamSelected} className="print:hidden">
          <CollapsibleListPanelBorder>
            <CollapsiblePanel expandedWidth="100%" disableTransition>
              {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                  child of `SquashPanel`, so the same rendered list responsively collapses into a
                  centered avatar column (icon rail) at narrow widths via container queries. */}
              <CollapsiblePanelContent>
                <Suspense fallback={<ListPanelBodySkeleton />}>
                  <TeamsPanelContent variant="sidebar" />
                </Suspense>
              </CollapsiblePanelContent>
            </CollapsiblePanel>
          </CollapsibleListPanelBorder>
        </CollapsibleListPanel>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target */}
        <SlideInPanel isOpen={hasTeamSelected} innerClassName="flex">
          {/* Middle Panel - Members List (auto-collapsed on mobile) */}
          <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={isMobile}>
            <StickyPanel className="print:hidden">
              <CollapsiblePanel>
                <Suspense fallback={<MembersPanelSkeleton />}>
                  <TeamsMembersPanel />
                </Suspense>
              </CollapsiblePanel>
            </StickyPanel>
          </CollapsiblePanelProvider>

          {/* Right Panel - Report/Distribution/Board or empty state (scrollable). Only mounts once a
              team is selected — `TeamReportContent` reads the team via `useTeam`, so it must
              never render without a teamId. With no team this slide-in panel is collapsed to width 0
              anyway, so rendering nothing here is invisible. */}
          <ScrollableContent>
            {hasTeamSelected ? (
              <Suspense fallback={<ReportPanelSkeleton />}>
                <TeamReportContent />
              </Suspense>
            ) : null}
          </ScrollableContent>
        </SlideInPanel>
      </div>
    </CollapsiblePanelProvider>
  )
}

function TeamReportContent() {
  const { t } = useLingui()
  const { first, second } = useRootSearch()
  const { team } = useTeam()

  const hasMembersInTeam = (team?.members?.length ?? 0) > 0
  const hasProfileSelected = first?.type === 'applicant' || second?.type === 'member' || second?.type === 'applicant'

  if (!hasMembersInTeam && !hasProfileSelected) {
    return (
      <ScrollableArea>
        <PanelEmptyState
          icon={<UsersIcon className="text-muted-foreground size-5" />}
          title={t`No members in this team`}
          description={t`Add members to get started`}
        />
      </ScrollableArea>
    )
  }

  return (
    <>
      <StickyHeader>
        <ReportTabs hasMembersInTeam={hasMembersInTeam} team={team} />
      </StickyHeader>
      <ScrollableArea>
        {/* The `teams.$teamId` layout route renders here via <AnimatedOutlet/>. It lives HERE (not in
            the `$teamId` layout) so the enter animation plays once when the report panel first appears
            and never re-animates on tab/team switches — see AnimatedOutlet. */}
        <AnimatedOutlet className="h-full" />
      </ScrollableArea>
    </>
  )
}

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

function TeamsBreadcrumbs() {
  const { t } = useLingui()
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild className={CRUMB_LINK_CLASS}>
            <Link
              to="/teams"
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {t`Teams`}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <Suspense fallback={null}>
          <TeamDetailCrumb />
        </Suspense>
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function TeamDetailCrumb() {
  const { teamId } = useParams({ strict: false })
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  if (!teamId) return null
  const team = data.list.find((tm) => tm.id === Number(teamId))
  if (!team?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink asChild className={CRUMB_LINK_CLASS}>
          <Link
            to="/teams/$teamId/report"
            params={{ teamId }}
            search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
          >
            {team.name}
          </Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
    </>
  )
}

function TeamsTitleActions() {
  return (
    <Suspense fallback={null}>
      <TeamsTitleActionsLoaded />
    </Suspense>
  )
}

function TeamsTitleActionsLoaded() {
  const { teamId } = useParams({ strict: false })
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  if (!teamId || isEmployee) return null
  const selectedTeam = data.list.find((tm) => tm.id === Number(teamId))
  if (!selectedTeam) return null
  return <TeamActionMenu team={selectedTeam} variant="header" />
}

type PanelTeam = {
  id: number
  name: string
  userId: number
  type: 'myTeams' | 'otherTeams'
  membersCount?: number
  function?: number | null
}

// Reads the loader-prefetched `getTeams` (see this route's `loader`) and feeds the rail list view.
function TeamsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { data } = useSuspenseQuery(trpc.teams.getTeams.queryOptions({}))
  return <TeamsPanelView variant={props.variant} teams={data.list} isEmployee={data.viewerRole === 'member'} />
}

function TeamsPanelView(props: { variant?: 'sidebar' | 'main'; teams: PanelTeam[]; isEmployee: boolean }) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  // Create is for the owner (a company account, or an admin in company view). An `authorized` employee
  // sees the company's teams read-only — the backend also rejects the write (Block A/C).
  const canCreateTeams = usePermissions().can('teams:write')
  const teams = props.teams
  const { teamId } = useParams({ strict: false })
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const navigate = useNavigate()

  // Clear search when a team is selected so the collapsed rail shows all teams
  useEffect(() => {
    if (teamId) setSearchQuery('')
  }, [teamId])

  const handleCreateTeam = () => {
    navigate({ to: '.', search: (prev) => ({ ...prev, editTeam: 'new' }) })
  }

  const filtered = teams.filter((team) => team.name.toLowerCase().includes(searchQuery.toLowerCase()))

  const myTeams = filtered.filter((team) => team.type === 'myTeams')
  const otherTeams = filtered.filter((team) => team.type === 'otherTeams')
  const allTeams = [...myTeams, ...otherTeams]
  const allTeamsCount = allTeams.length
  const myTeamsCount = myTeams.length
  const otherTeamsCount = otherTeams.length

  const showOnlyEmptyState = teams.length === 0 && !searchQuery

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    // Main panel variant - full UI adapted for main content area
    return (
      <div className="flex h-full flex-col">
        {!showOnlyEmptyState && (
          <>
            {/* Search bar with create button. Employees are view-only — they can't create teams. */}
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
              {!isEmployee && canCreateTeams && (
                <Button variant="secondary" onClick={handleCreateTeam}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Teams list */}
        <ScrollableList
          isEmpty={allTeams.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allTeams.length === 0 && !!searchQuery}
              icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
              title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
              description={isEmployee ? undefined : t`Add teams to get started`}
              action={
                !isEmployee && canCreateTeams ? (
                  <Button variant="secondary" onClick={handleCreateTeam}>
                    <PlusIcon className="size-4" />
                    {t`Add team`}
                  </Button>
                ) : undefined
              }
            />
          }
          hint={
            allTeams.length > 0 && (
              <SelectionHint visible={!teamId} text={t`Select a team to view its members and reports`} />
            )
          }
        >
          <SectionLabel className="px-2">
            {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
          </SectionLabel>
          {isEmployee ? (
            // Employees don't own teams in this version, so the "my teams" vs "teams that added me"
            // split is meaningless to them — show every assigned team as a single flat list.
            allTeams.map((team) => (
              <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
            ))
          ) : (
            <>
              {/* My Teams section */}
              {myTeams.length > 0 && (
                <>
                  {otherTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                      </SectionLabel>
                    </div>
                  )}
                  {myTeams.map((team) => (
                    <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                  ))}
                </>
              )}

              {/* Other Teams section */}
              {otherTeams.length > 0 && (
                <>
                  {myTeams.length > 0 && (
                    <div className="p-2">
                      <SectionLabel>
                        {otherTeams.length === 1
                          ? t`${otherTeamsCount} team that added me`
                          : t`${otherTeamsCount} teams that added me`}
                      </SectionLabel>
                    </div>
                  )}
                  {otherTeams.map((team) => (
                    <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                  ))}
                </>
              )}
            </>
          )}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search onSearchClick={() => setSearchOpen(true)} searchLabel={t`Search Teams`}>
              <SearchInput placeholder={t`Search Teams`} value={searchQuery} onChange={setSearchQuery} />
            </CollapsibleToolbar.Search>
            {/* Employees are view-only — no create action. */}
            {!isEmployee && canCreateTeams && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                label={t`Add`}
                tooltip={t`Add team`}
                onClick={handleCreateTeam}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isEmpty={allTeams.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allTeams.length === 0 && !!searchQuery}
            icon={<NAVIGATION_ICONS.teams className="text-muted-foreground size-5" />}
            title={isEmployee ? t`You haven't been added to any team` : t`No teams yet`}
            description={isEmployee ? undefined : t`Add teams to get started`}
            action={
              !isEmployee && canCreateTeams ? (
                <Button variant="secondary" onClick={handleCreateTeam}>
                  <PlusIcon className="size-4" />
                  {t`Add team`}
                </Button>
              ) : undefined
            }
          />
        }
        hint={
          allTeams.length > 0 && (
            <SelectionHint visible={!teamId} text={t`Select a team to view its members and reports`} />
          )
        }
      >
        {isEmployee ? (
          // Employees don't own teams — show every assigned team in one flat list (no my/other split).
          <>
            <CollapsibleSectionLabel>
              {allTeamsCount === 1 ? t`${allTeamsCount} team` : t`${allTeamsCount} teams`}
            </CollapsibleSectionLabel>
            {allTeams.map((team) => (
              <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
            ))}
          </>
        ) : (
          <>
            {myTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {myTeams.length === 1 ? t`${myTeamsCount} team` : t`${myTeamsCount} teams`}
                </CollapsibleSectionLabel>
                {myTeams.map((team) => (
                  <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                ))}
              </>
            )}

            {otherTeams.length > 0 && (
              <>
                <CollapsibleSectionLabel>
                  {otherTeams.length === 1
                    ? t`${otherTeamsCount} team that added me`
                    : t`${otherTeamsCount} teams that added me`}
                </CollapsibleSectionLabel>
                {otherTeams.map((team) => (
                  <TeamTreeItem key={team.id} team={team} isSelected={teamId === team.id.toString()} />
                ))}
              </>
            )}
          </>
        )}
      </ScrollableList>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} title={t`Search Teams`}>
        <Command className="gap-1">
          <CommandInput placeholder={t`Search Teams`} />
          <CommandList>
            <CommandEmpty>{t`No teams found`}</CommandEmpty>
            {allTeams.map((team) => (
              <CommandItem
                key={team.id}
                value={`${team.name} ${team.id}`}
                onSelect={() => {
                  navigate({
                    to: '/teams/$teamId/report',
                    params: { teamId: team.id.toString() },
                    search: (prev) => ({
                      ...prev,
                      q: undefined,
                      first: undefined,
                      second: undefined,
                      selected: undefined,
                    }),
                  })
                  setSearchOpen(false)
                }}
              >
                <Avatar size="sm" type="team" name={team.name} />
                <span className="truncate">{team.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}
